import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IMigratedDataSummary,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  AccountCredit,
  Brand,
  BuildAccountAggregates,
  Invoice,
  Patient,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  ITreatmentConfiguration,
  type IAccountCredit,
  type IBasePatient,
  type IBrand,
  type IInvoice,
  type IPatient,
  type IPatientContactDetails,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  doc,
  getError,
  omitByKeys,
  resolveSequentially,
  runTransaction,
  safeCombineLatest,
  snapshotCombineLatest,
  toTimestamp,
  type DocumentReference,
  type IIdentifiable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sumBy } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  PATIENT_INVOICE_RESOURCE_TYPE,
  PATIENT_TRANSACTION_RESOURCE_TYPE,
} from '../../../destination/entities/patient-invoices';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import { PatientBalanceSourceEntity } from '../../source/entities/patient-balance';
import { PatientTransactionsSourceEntity } from '../../source/entities/patient-transactions';
import { PatientTreatmentSourceEntity } from '../../source/entities/patient-treatments';
import { ExactItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { ExactItemCodeMappingHandler } from '../mappings/item-codes';
import { StaffToPracticeMapping } from '../mappings/practitioner-to-practice-mapping';
import { ExactStafferMappingHandler } from '../mappings/staff';
import { ExactInvoiceBuilder } from './lib/exact-invoice-builder';
import { PatientDestinationEntity } from './patient';

interface IInvoiceUpdateData {
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
  accountCreditRefs: DocumentReference<IAccountCredit>[];
}

export const PATIENT_INVOICE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_INVOICE_RESOURCE_TYPE,
    label: 'Patient Invoices',
    description: `
      Exact has a flat table of transactions (incoming and outgoing) and does not track any payments back to any charges. To deal with this, any charges made on the same day are
      grouped into an invoice and their line items will be built by any found treatments (or the description as it exists in Exact if nothing is linked).

      Afterwards, all payments are ordered by date and we apply payments to the oldest invoices first. Any invoices that do not balance will all be the most recent ones. Any overpayments found
      at the end of this processing will be added to the last invoice in the order and will be added as deposit line items, the result will be patients that show credits in Principle.
    `,
  },
});

interface IInvoiceBuildData {
  invoice: IIdentifiable & IInvoice;
  transactions: (IIdentifiable & ITransaction)[];
}

interface IPatientInvoiceMigrationData {
  patientRef: DocumentReference<IPatient>;
  data: IInvoiceBuildData[];
  createdAt: Timestamp;
}

export interface IPatientInvoiceDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord<ExactPatient>>;
  invoiceData: {
    invoiceRef: DocumentReference<IInvoice>;
    transactionRefs: DocumentReference<ITransaction>[];
  }[];
}

export interface IPatientInvoiceJobData {
  sourcePatient: IGetRecordResponse<ExactPatient>;
  brand: WithRef<IBrand>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: WithRef<IStaffer>[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
  sourceItemCodesToTreatments: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[];
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
}

export class PatientInvoicesDestinationEntity extends BaseDestinationEntity<
  IPatientInvoiceDestinationRecord,
  IPatientInvoiceJobData,
  IPatientInvoiceMigrationData
> {
  destinationEntity = PATIENT_INVOICE_DESTINATION_ENTITY;

  sourceCountComparison = new PatientSourceEntity();

  override canMigrateByIdRange = true;

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    transactions: new PatientTransactionsSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
    balances: new PatientBalanceSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  override filters = [
    new PatientIdFilter<IPatientInvoiceJobData>(
      (data) => data.sourcePatient.data.patient_id
    ),
  ];

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    itemCodeToTreatments: new ExactItemCodeToTreatmentMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
  };

  sourceCountDataAccessor(
    data: IPatientInvoiceJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientInvoiceDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest(
      record.data.invoiceData.map((data) =>
        combineLatest([
          Firestore.getDoc(data.invoiceRef),
          safeCombineLatest(
            data.transactionRefs.map((transactionRef) =>
              Firestore.getDoc(transactionRef)
            )
          ),
        ])
      )
    ).pipe(
      map(([invoiceRef, transactions]) => {
        const data: IMigratedDataSummary[] = [
          {
            label: 'Invoice',
            data: invoiceRef,
          },
        ];

        data.push(
          ...transactions.map((transaction) => ({
            label: 'Transactions',
            data: transaction,
          }))
        );

        return data;
      })
    );
  }

  getDestinationEntityRecordUid(data: IPatientInvoiceJobData): string {
    return data.sourcePatient.record.uid;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInvoiceJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration);
    const practitioners$ = brand$.pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand)))
    );

    return snapshotCombineLatest([
      staff$,
      brand$,
      practitioners$,
      this.customMappings.itemCodes.getRecords(translationMap),
      this.customMappings.itemCodeToTreatments.getRecords(translationMap),
      this.customMappings.staffToPractice.getRecords(translationMap),
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          patients,
          [
            staff,
            brand,
            practitioners,
            sourceItemCodes,
            sourceItemCodesToTreatments,
            staffToPractice,
          ],
        ]) =>
          patients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            brand,
            practitioners,
            sourceItemCodes,
            sourceItemCodesToTreatments,
            staffToPractice,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInvoiceJobData
  ): Promise<
    | IPatientInvoiceMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const patientId = data.sourcePatient.data.patient_id;
    const patientRef = await translationMap.getDestination<IPatient>(
      patientId,
      PATIENT_RESOURCE_TYPE
    );
    if (!patientRef) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    const patient = await Firestore.getDoc(patientRef);
    const patientTransactions =
      await this.sourceEntities.transactions.filterRecords(
        migration,
        'patientId',
        patientId
      );

    if (!patientTransactions.length) {
      // eslint-disable-next-line no-console
      console.error(`No transactions found for patient ${patientId}`);
      return {
        patientRef: patient.ref,
        data: [],
        createdAt: Timestamp.now(),
      };
    }

    const patientBalances = await this.sourceEntities.balances.filterRecords(
      migration,
      'patientId',
      patientId
    );

    const balance = patientBalances.length
      ? sumBy(patientBalances, (patientBalance) => patientBalance.data.balance)
      : 0;

    const patientTreatments =
      await this.sourceEntities.treatments.filterRecords(
        migration,
        'patientId',
        patientId
      );

    const invoiceBuilder = new ExactInvoiceBuilder(
      patientTransactions,
      patientTreatments,
      balance,
      data.staff,
      data.sourceItemCodes,
      data.sourceItemCodesToTreatments,
      data.staffToPractice
    );

    try {
      await invoiceBuilder.buildAccountSummary(
        migration.configuration.practices,
        translationMap
      );

      const invoiceData = await invoiceBuilder.buildInvoices(
        patient as IBasePatient & IPatientContactDetails,
        translationMap,
        migration.configuration.practices
      );

      return {
        patientRef: patient.ref,
        data: invoiceData,
        createdAt: Timestamp.now(),
      };
    } catch (error) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientInvoiceJobData,
    _migrationData: IPatientInvoiceMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<ExactPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage: errorMessage ?? 'Missing required properties for patient',
      failData: {
        patientRef: patient.id,
      },
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IPatientInvoiceJobData,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const invoiceRefs = await this._upsertInvoices(
        migrationData,
        translationMap,
        migrationData.patientRef
      );

      const accountAggregateBuilder = new BuildAccountAggregates(console);
      await accountAggregateBuilder.updatePatient(
        migrationData.patientRef,
        false
      );

      return this._buildSuccessResponse(jobData.sourcePatient, invoiceRefs);
    } catch (error) {
      return this.buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IPatientInvoiceMigrationData | undefined> {
    const invoiceMergeConflicts: (IInvoiceBuildData | undefined)[] =
      await resolveSequentially(
        migrationData.data,
        async (invoiceBuildData) => {
          try {
            const invoiceRef = await translationMap.getDestination<IInvoice>(
              invoiceBuildData.invoice.uid,
              PATIENT_TRANSACTION_RESOURCE_TYPE
            );

            if (!invoiceRef) {
              return;
            }

            const existingInvoice = await Firestore.getDoc(invoiceRef);
            const invoiceMergeConflict = hasMergeConflicts(
              omitByKeys(invoiceBuildData.invoice, ['from', 'to']),
              omitByKeys(existingInvoice, ['from', 'to']),
              [],
              [
                {
                  key: 'transactions',
                  sortByPath: 'reference',
                },
              ]
            );

            const existingTransactions: (IIdentifiable & ITransaction)[] = [];

            const transactionMergeConflicts = await resolveSequentially(
              invoiceBuildData.transactions,
              async (transaction) => {
                try {
                  const existingTransaction = await Firestore.getDoc(
                    doc(
                      Invoice.transactionCol(existingInvoice),
                      transaction.uid
                    )
                  );

                  existingTransactions.push({
                    ...existingTransaction,
                  });

                  return hasMergeConflicts(transaction, existingTransaction);
                } catch {
                  return false;
                }
              }
            );

            const hasMergeConflict = [
              invoiceMergeConflict,
              ...transactionMergeConflicts,
            ].some((mergeConflict) => mergeConflict);

            if (hasMergeConflict) {
              return {
                ...invoiceBuildData,
                invoice: {
                  ...existingInvoice,
                  uid: invoiceBuildData.invoice.uid,
                },
                transactions: existingTransactions,
              };
            }
          } catch {
            return;
          }
        }
      );

    if (invoiceMergeConflicts.some((conflict) => conflict)) {
      return {
        ...migrationData,
        data: compact(invoiceMergeConflicts),
      };
    }
  }

  buildSkippedResponse(
    data: IPatientInvoiceJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  // TODO: CU-219c758 - Do we want to set invoice id on the treatment?
  private async _upsertInvoices(
    migrationData: IPatientInvoiceMigrationData,
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<IInvoiceUpdateData[]> {
    return runTransaction(async (firestoreTransaction) => {
      return resolveSequentially(
        migrationData.data,
        async (invoiceBuildData) => {
          const invoiceDestinationRef = await translationMap.getDestination(
            invoiceBuildData.invoice.uid,
            PATIENT_TRANSACTION_RESOURCE_TYPE
          );

          const invoiceRef = await FirestoreMigrate.upsertDoc(
            Patient.invoiceCol({
              ref: patientRef,
            }),
            { ...invoiceBuildData.invoice },
            invoiceDestinationRef?.id,
            firestoreTransaction
          );

          if (!invoiceDestinationRef) {
            await translationMap.upsert({
              sourceIdentifier: invoiceBuildData.invoice.uid,
              destinationIdentifier: invoiceRef,
              resourceType: PATIENT_TRANSACTION_RESOURCE_TYPE,
            });
          }

          const transactionRefs = await resolveSequentially(
            invoiceBuildData.transactions,
            (transaction) =>
              FirestoreMigrate.upsertDoc(
                Invoice.transactionCol({
                  ref: invoiceRef,
                }),
                transaction,
                transaction.uid,
                firestoreTransaction
              )
          );

          const existingCredits = await Firestore.getDocs(
            AccountCredit.col({ ref: patientRef })
          );
          const accountCredits = Invoice.getDueCredits(
            { ...invoiceBuildData.invoice, ref: invoiceRef },
            existingCredits.filter((credit) => !credit.deleted)
          );

          const accountCreditRefs = await resolveSequentially(
            accountCredits,
            (accountCredit) =>
              FirestoreMigrate.upsertDoc(
                AccountCredit.col({
                  ref: patientRef,
                }),
                { ...accountCredit, deleted: false },
                accountCredit.depositUid,
                firestoreTransaction
              )
          );

          return {
            invoiceRef,
            transactionRefs,
            accountCreditRefs,
          };
        }
      );
    });
  }

  private _buildSuccessResponse(
    patient: IGetRecordResponse<ExactPatient>,
    updateData?: IInvoiceUpdateData[]
  ): IDestinationEntityRecord<IPatientInvoiceDestinationRecord> {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      data: {
        sourceRef: patient.record.id,
        invoiceData: updateData ?? [],
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
